import type {
  DragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import type { CodeEditorDraftEntry } from "@/stores/slices/code-editor.slice";
import { FileTree, type FileTreeNode } from "../file-tree";

type FileExplorerProps = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  filteredTree: Array<FileTreeNode>;
  isSearchActive: boolean;
  highlightMatches: (text: string) => ReactNode;
  expandedDirectories: Set<string>;
  onToggleDirectory: (path: string) => void;
  selectedFile: string | null;
  onSelectFile: (path: string) => void;
  drafts: Record<string, CodeEditorDraftEntry>;
  dragHandlers: {
    draggedDirectory: string | null;
    onDirectoryDragOver: (
      event: DragEvent<HTMLDivElement>,
      path: string,
    ) => void;
    onDirectoryDragLeave: (
      event: DragEvent<HTMLDivElement>,
      path: string,
    ) => void;
    onDirectoryDrop: (event: DragEvent<HTMLDivElement>, path: string) => void;
  };
  isLoading: boolean;
  isRefetching: boolean;
  hasError: boolean;
  onRootDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onRootDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onRootDrop: (event: DragEvent<HTMLDivElement>) => void;
  isRootDragTarget: boolean;
};

export function FileExplorer({
  searchQuery,
  onSearchQueryChange,
  onSearchKeyDown,
  filteredTree,
  isSearchActive,
  highlightMatches,
  expandedDirectories,
  onToggleDirectory,
  selectedFile,
  onSelectFile,
  drafts,
  dragHandlers,
  isLoading,
  isRefetching,
  hasError,
  onRootDragOver,
  onRootDragLeave,
  onRootDrop,
  isRootDragTarget,
}: FileExplorerProps) {
  return (
    <div className="w-72 border-r border-border bg-muted/20 flex h-full min-h-0 flex-col">
      <div className="h-10 flex items-center px-3 border-b border-border bg-muted/30 text-xs font-medium uppercase tracking-wide">
        Files
      </div>
      <div className="px-3 py-2 border-b border-border bg-muted/10">
        <div className="relative">
          <Input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search files"
            aria-label="Search files"
            className="h-8 text-xs"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div
          className={cn(
            "py-2 space-y-1",
            isRootDragTarget &&
              "bg-primary/5 border border-dashed border-primary/40 rounded-sm mx-2 px-1",
          )}
          onDragOver={(event) => {
            if (event.currentTarget !== event.target) return;
            event.preventDefault();
            onRootDragOver(event);
          }}
          onDragLeave={(event) => {
            if (event.currentTarget !== event.target) return;
            onRootDragLeave(event);
          }}
          onDrop={(event) => {
            if (event.currentTarget !== event.target) return;
            event.preventDefault();
            onRootDrop(event);
          }}
        >
          {(isLoading || isRefetching) && filteredTree.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Loading files...
            </div>
          ) : null}
          {hasError ? (
            <div className="px-3 py-2 text-xs text-destructive">
              Unable to load files.
            </div>
          ) : null}
          {!isLoading && !hasError && filteredTree.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {isSearchActive ? "No matching files." : "No files available."}
            </div>
          ) : null}
          {!isLoading && !hasError ? (
            <FileTree
              nodes={filteredTree}
              expandedDirectories={expandedDirectories}
              onToggleDirectory={onToggleDirectory}
              selectedFile={selectedFile}
              onSelectFile={onSelectFile}
              drafts={drafts}
              highlightMatches={highlightMatches}
              draggedDirectory={dragHandlers.draggedDirectory}
              onDirectoryDragOver={dragHandlers.onDirectoryDragOver}
              onDirectoryDragLeave={dragHandlers.onDirectoryDragLeave}
              onDirectoryDrop={dragHandlers.onDirectoryDrop}
            />
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}
